// @ts-nocheck
/* eslint-enable */
/* eslint-disable camelcase */
// Misc / utils
import searchSlugUtils from 'app/shared/utils/searchSlugUtils';
import { areaUtils_getAffordableOrLuxurySlugPageLimit } from 'app/shared/utils/areaUtils';
import constants from 'app/shared/constants/ValueConstants';

export const useListingHeader = () => {
  const getScreenReaderHeader = ({ areaName, searchSlug, totalListings }) => {
    const description = searchSlugUtils.prepareSlugDescription(searchSlug);
    let primaryHeader = 'Apartments for rent';

    if (!totalListings || !description || !areaName) {
      return [primaryHeader];
    }

    totalListings = totalListings.toLocaleString('en-US', { minimumFractionDigits: 0 });
    primaryHeader = `${totalListings} ${description} near ${areaName}`;

    return [primaryHeader];
  };

  const getAreaTitle = ({
    areaType,
    areaName,
    areaFullName,
    areaCity,
    areaState,
    hasMapBorder = true,
    slugTitle = 'Apartments for Rent',
  }: {
    areaType: string;
    areaName: string | number;
    areaFullName: string | number;
    areaCity: string;
    areaState: string;
    hasMapBorder?: boolean;
    slugTitle?: string;
  }) => {
    const isZipArea = areaType === 'zip';
    const zipCodeAndAreaName = `Zip Code ${areaName}`;
    const areaTypeAndFullName = isZipArea ? zipCodeAndAreaName : areaFullName;
    const areaTypeAndName = isZipArea ? zipCodeAndAreaName : areaName;
    const areaNameAndCity = isZipArea ? zipCodeAndAreaName : `${areaName} ${areaCity}`;
    const schoolTypes = ['primaryschool', 'middleschool', 'mixedschool', 'highschool', 'university', 'unifschdist'];
    const isInSchoolZone = schoolTypes.includes(areaType);
    const city = areaCity;
    const state = areaState;
    const schoolLocation = `${city ? `${city}, ${state}` : `${state}`}`;
    let pageHeaderArray;

    const TITLE_MAP = {
      'Apartments for Rent': {
        true: [areaTypeAndFullName, isInSchoolZone ? schoolLocation : null, 'Apartments for Rent'],
        false: ['Apartments for Rent near', isInSchoolZone ? schoolLocation : null, areaTypeAndFullName],
      },
      'Studio Apartments for Rent': {
        true: [
          `Studio Apartments for Rent ${isInSchoolZone ? `near` : `in`}`,
          areaTypeAndFullName,
          isInSchoolZone ? schoolLocation : null,
        ],
        false: ['Studio Apartments for Rent near', areaName, isInSchoolZone ? schoolLocation : null],
      },
      'Affordable Apartments for Rent': {
        true: [
          `Affordable Apartments for Rent ${isInSchoolZone ? 'near' : 'in'}`,
          `${areaType === 'neighborhood' ? areaNameAndCity : areaTypeAndName}${isInSchoolZone ? '' : `, ${state}`}`,
          isInSchoolZone ? schoolLocation : null,
        ],
        false: ['Affordable Apartments for Rent near', areaName, isInSchoolZone ? schoolLocation : null],
      },
      'Affordable Houses for Rent': {
        true: [
          `Affordable Houses for Rent ${isInSchoolZone ? 'near' : 'in'}`,
          `${areaType === 'neighborhood' ? areaNameAndCity : areaTypeAndName}${isInSchoolZone ? '' : `, ${state}`}`,
          isInSchoolZone ? schoolLocation : null,
        ],
        false: ['Affordable Houses for Rent near', areaName, isInSchoolZone ? schoolLocation : null],
      },
      'For Rent By Owner': {
        true: [
          `For Rent by Private Owner ${isInSchoolZone ? 'near' : 'in'}`,
          `${areaType === 'neighborhood' ? areaNameAndCity : areaTypeAndFullName}`,
          isInSchoolZone ? schoolLocation : null,
        ],
        false: ['Private Owner Rentals (FRBO) near', areaName, isInSchoolZone ? schoolLocation : null],
      },
      'Houses for Rent By Owner': {
        true: [
          `Houses for Rent by Private Owner ${isInSchoolZone ? 'near' : 'in'}`,
          `${areaType === 'neighborhood' ? areaNameAndCity : areaTypeAndFullName}`,
          isInSchoolZone ? schoolLocation : null,
        ],
        false: ['Private Owner Rental Houses (FRBO) near', areaName, isInSchoolZone ? schoolLocation : null],
      },
      'Apartments and Condos for Rent By Owner': {
        true: [
          `Apartments and Condos for Rent by Private Owner ${isInSchoolZone ? 'near' : 'in'}`,
          `${areaType === 'neighborhood' ? areaNameAndCity : areaTypeAndFullName}`,
          isInSchoolZone ? schoolLocation : null,
        ],
        false: [
          'Private Owner Rental Apartments and Condos (FRBO)  near',
          areaName,
          isInSchoolZone ? schoolLocation : null,
        ],
      },
      'Townhomes for Rent By Owner': {
        true: [
          `Private Owner Rental Townhomes (FRBO) ${isInSchoolZone ? 'near' : 'in'}`,
          `${areaType === 'neighborhood' ? areaNameAndCity : areaTypeAndFullName}`,
          isInSchoolZone ? schoolLocation : null,
        ],
        false: ['Private Owner Rental Townhomes (FRBO) near', areaName, isInSchoolZone ? schoolLocation : null],
      },
      'New Apartments for Rent': {
        true: [
          'New',
          `${areaType === 'neighborhood' ? areaNameAndCity : areaTypeAndName}${isInSchoolZone ? '' : `, ${state}`}`,
          'Apartments',
          isInSchoolZone ? schoolLocation : null,
          'Available Now',
        ],
        false: ['New Apartments near', areaName, isInSchoolZone ? schoolLocation : null, 'Available Now'],
      },
      'New Houses for Rent': {
        true: [
          'Newest',
          `${areaType === 'neighborhood' ? areaNameAndCity : areaTypeAndName}${isInSchoolZone ? '' : `, ${state}`}`,
          'Houses',
          isInSchoolZone ? schoolLocation : null,
          'Available Now',
        ],
        false: ['Newest houses near', areaName, isInSchoolZone ? schoolLocation : null, 'Available Now'],
      },
      zip: {
        true: [slugTitle, 'in', areaTypeAndFullName],
        false: [slugTitle, 'near', areaTypeAndFullName],
      },
      default: {
        true: isInSchoolZone ? [slugTitle, 'near', areaName, schoolLocation] : [areaTypeAndFullName, slugTitle],
        false: [slugTitle, 'near', areaName, isInSchoolZone ? schoolLocation : null],
      },
    };

    /**
     * areaFullName: `Seattle, WA`, `94015`
     * areaName: `Seattle`, `94103`
     */
    if (areaType === 'zip') {
      const lowerCaseSlugTitle = slugTitle?.toLowerCase();
      // Any for-rent-by-owner slug EXCEPT townhomes-for-rent-by-owner
      const isForRentByOwnerExceptTownhomes =
        lowerCaseSlugTitle?.includes('for rent by owner') && !lowerCaseSlugTitle?.includes('townhomes');
      if (isForRentByOwnerExceptTownhomes) {
        pageHeaderArray = TITLE_MAP[slugTitle][hasMapBorder];
      } else {
        pageHeaderArray = TITLE_MAP.zip[hasMapBorder];
      }
    } else {
      pageHeaderArray = TITLE_MAP[slugTitle] ? TITLE_MAP[slugTitle][hasMapBorder] : TITLE_MAP.default[hasMapBorder];
    }

    const filteredHeadingArray = pageHeaderArray.filter((el) => {
      return el !== null;
    }); // Removes null values

    return filteredHeadingArray.join(' ');
  };

  const getMainPagingText = ({ currentPage, totalBuildings, searchSlug }) => {
    const maxPage = Math.max(Math.ceil(Number(totalBuildings) / constants.MAX_LISTINGS_PER_PAGE), 1);
    const formattedPagination = `Page ${currentPage} / ${maxPage}: `;

    if (searchSlugUtils.isAffordableOrLuxSearchSlug(searchSlug)) {
      const affordableOrLuxFormattedPagination = `Page ${currentPage} / ${areaUtils_getAffordableOrLuxurySlugPageLimit(
        maxPage,
      )}: `;
      return affordableOrLuxFormattedPagination;
    }

    return formattedPagination;
  };

  const getSecondaryPagingText = ({ totalListings, slugTitle }) => {
    const formattedTotalListings = totalListings.toLocaleString('en-US', { minimumFractionDigits: 0 });
    const formattedTitle = slugTitle.toLowerCase();
    return `${formattedTotalListings} ${formattedTitle}`;
  };

  return [getScreenReaderHeader, getAreaTitle, getMainPagingText, getSecondaryPagingText] as const;
};
